import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthService } from '../../auth/services/auth.service';
import { getErrorBody } from '../helpers/get-error-body.helper';

/**
 * This is used to logout the user, when the server responds with an unathorized status code.
 * Especially when the session token expires.
 * @export
 * @class ErrorInterceptor
 * @implements {HttpInterceptor}
 */
@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  constructor(private authService: AuthService) {}

  /**
   * Intercepter intercepts the responses, and then process based on the recieved status code
   * @param {HttpRequest<any>} request
   * @param {HttpHandler} next
   * @returns {Observable<HttpEvent<any>>}
   * @memberof ErrorInterceptor
   */
  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.status === 401) {
          // auto logout if 401 response returned from api
          this.authService.logout();
        }

        // Keep full HttpErrorResponse for shared-scenario conflicts (409)
        // so callers can access structured payload fields.
        if (isSharedScenarioConflict(err)) {
          const sharedError = getErrorBody(err.error);
          if (sharedError) {
            return throwError(() => ({
              ...sharedError,
              status: err.status,
              statusText: err.statusText
            }));
          }

          return throwError(() => err.error);
        }

        // The string type is required to be extended, because the error object is a string
        // however required to add the status code to the error object
        // Making the interceptor backwards compatible yet able to access the status code
        const error = new String(
          // err.error is not null, if the ResponsenEntity contains an Exception
          // err.error.message will give the custom message send from the server
          err.error?.message || err.statusText
        ) as AugmentedError;
        error.status = err.status;
        return throwError(() => error);
      })
    );
  }
}

// Object is created to extend the string type
// This is used to add the status code to the error object
// yet enabling the string type to be used as a string
export type AugmentedError = string & {
  status?: number;
};

function isSharedScenarioConflict(err: HttpErrorResponse): boolean {
  if (err.status !== 409) {
    return false;
  }

  const body = getErrorBody(err.error);
  return !!body && typeof body.sharedScenarioName === 'string';
}
